import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LuArrowRight, LuBell, LuFileText, LuFileUp, LuTruck } from 'react-icons/lu';
import Button from '../ui/Button';
import GlassIcon from '../ui/GlassIcon';
import Modal from '../ui/Modal';
import { useTranslation } from '../../i18n/LanguageContext';
import useMissingDocuments, {
  SOP_PROMPT_SESSION_KEY,
  readSessionFlag,
  writeSessionFlag,
} from './useMissingDocuments';
import useActiveCargo from './useActiveCargo';
import CargoReminderItem from './CargoReminderItem';
import { BADGE, BADGE_BASE, ITEM } from './reminderStyles';

const MAX_VISIBLE_SOPS = 4;
const MAX_VISIBLE_CARGO = 4;

const TONES = {
  danger: { bar: 'tw-border-l-rose-500', icon: 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300', count: 'tw-bg-rose-500', button: 'tw-from-rose-500 tw-to-red-600 tw-shadow-rose-500/30' },
  warning: { bar: 'tw-border-l-amber-500', icon: 'tw-bg-amber-400/15 tw-text-amber-600 dark:tw-text-amber-300', count: 'tw-bg-amber-500', button: 'tw-from-amber-500 tw-to-orange-600 tw-shadow-amber-500/30' },
  info: { bar: 'tw-border-l-sky-500', icon: 'tw-bg-sky-500/10 tw-text-sky-600 dark:tw-text-sky-300', count: 'tw-bg-sky-500', button: 'tw-from-sky-500 tw-to-blue-600 tw-shadow-sky-500/30' },
};

const ReminderSection = ({ tone, icon: Icon, title, count, description, actionLabel, onAction, children }) => {
  const t = TONES[tone];
  return (
    <section className={`tw-space-y-3 tw-rounded-2xl tw-border tw-border-l-4 tw-border-slate-200/80 tw-bg-white/80 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.03] ${t.bar}`}>
      <div className="tw-flex tw-items-start tw-gap-3">
        <span className={`tw-grid tw-h-9 tw-w-9 tw-shrink-0 tw-place-items-center tw-rounded-xl ${t.icon}`}><Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" /></span>
        <div className="tw-min-w-0">
          <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">
            {title}
            <span className={`tw-rounded-full tw-px-2 tw-py-0.5 tw-text-[11px] tw-font-bold tw-text-white ${t.count}`}>{count}</span>
          </h3>
          <p className="tw-mt-0.5 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{description}</p>
        </div>
      </div>
      <ul className="tw-space-y-1.5">{children}</ul>
      <div className="tw-flex tw-justify-end">
        <button type="button" onClick={onAction} className={`tw-inline-flex tw-h-10 tw-items-center tw-gap-2 tw-rounded-xl tw-bg-gradient-to-b tw-px-4 tw-text-sm tw-font-semibold tw-text-white tw-shadow-lg tw-transition hover:tw-brightness-105 ${t.button}`}>
          {actionLabel}
          <LuArrowRight aria-hidden="true" className="tw-h-4 tw-w-4" />
        </button>
      </div>
    </section>
  );
};

/**
 * Girişte bir kez çıkan hatırlatmaları (okunmamış SOP'lar, eksik CV/GCP/IATA
 * belgeleri, bekleyen kargolar) TEK bir pencerede toplar. Pencere, tüm kontroller sonuçlandıktan
 * sonra açılır; böylece hatırlatmalar üst üste binmez ve sonradan büyümez.
 */
const LoginReminders = ({ unreadSOPs, sopsLoaded, onGoToSops }) => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`loginReminders.${key}`, vars);
  const { missing, settled, signedIn, markShown } = useMissingDocuments();
  const { cargo, settled: cargoSettled, markShown: markCargoShown } = useActiveCargo();
  // null: kapalı; aksi halde hangi bölümlerin gösterileceği
  const [sections, setSections] = useState(null);

  useEffect(() => {
    if (!signedIn) {
      setSections(null);
      return;
    }
    if (sections || !sopsLoaded || !settled || !cargoSettled) return;

    const showSops = unreadSOPs.length > 0 && !readSessionFlag(SOP_PROMPT_SESSION_KEY);
    const showDocs = missing.length > 0;
    const showCargo = cargo.length > 0;
    if (!showSops && !showDocs && !showCargo) return;

    setSections({ sops: showSops, docs: showDocs, cargo: showCargo });
  }, [signedIn, sections, sopsLoaded, settled, cargoSettled, unreadSOPs.length, missing.length, cargo.length]);

  // "Gösterildi" işareti pencere açılınca değil, kullanıcı kapatınca yazılır:
  // Layout her rotada yeniden kurulduğundan, görülmeden kaybolan bir pencere
  // sonraki sayfada yeniden açılır.
  const close = useCallback(() => {
    if (sections?.sops) writeSessionFlag(SOP_PROMPT_SESSION_KEY);
    if (sections?.docs) markShown();
    if (sections?.cargo) markCargoShown();
    setSections({ sops: false, docs: false, cargo: false });
  }, [sections, markShown, markCargoShown]);

  const isOpen = Boolean(sections && (sections.sops || sections.docs || sections.cargo));

  // Escape and backdrop clicks are handled by Modal (onClose)

  if (!isOpen) return null;

  const goToSops = () => {
    close();
    onGoToSops();
  };

  const goToDocuments = () => {
    close();
    // İlk eksik belgenin sekmesi açık gelsin
    navigate('/profile', { state: { activeTab: missing[0].profileTab } });
  };

  const goToCargo = () => {
    close();
    navigate('/profile', { state: { activeTab: 'kargolarim' } });
  };

  const topicCount = Number(sections.sops) + Number(sections.docs) + Number(sections.cargo);
  const onlyCargo = sections.cargo && topicCount === 1;

  return (
    <Modal
      isOpen
      onClose={close}
      size="md"
      title={onlyCargo ? label('cargo.title') : label('title')}
      subtitle={label('subtitle', { count: topicCount })}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-indigo-400 tw-to-violet-600 tw-shadow-indigo-500/30" size="xs">
          <LuBell aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={<Button variant="ghost" onClick={close}>{label('later')}</Button>}
    >
      <div className="tw-space-y-3">
        {sections.sops && (
          <ReminderSection tone="danger" icon={LuFileText} title={label('sops.title')} count={unreadSOPs.length}
            description={label('sops.description')} actionLabel={label('sops.action')} onAction={goToSops}>
            {unreadSOPs.slice(0, MAX_VISIBLE_SOPS).map((item) => (
              <li key={item.id} className={ITEM}>
                <span className="tw-min-w-0 tw-truncate" title={item.name}>{item.name}</span>
                {item.dueDate && <span className={`${BADGE_BASE} ${item.dueSoon ? BADGE.urgent : BADGE.neutral}`}>{item.deadlineLabel}</span>}
              </li>
            ))}
            {unreadSOPs.length > MAX_VISIBLE_SOPS && <li className="tw-px-3.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('sops.more', { count: unreadSOPs.length - MAX_VISIBLE_SOPS })}</li>}
          </ReminderSection>
        )}

        {sections.docs && (
          <ReminderSection tone="warning" icon={LuFileUp} title={label('docs.title')} count={missing.length}
            description={label('docs.description')} actionLabel={label('docs.action')} onAction={goToDocuments}>
            {missing.map((type) => (
              <li key={type.key} className={ITEM}>
                <span className="tw-min-w-0 tw-truncate">{type.label}</span>
                <span className={`${BADGE_BASE} ${BADGE.warning}`}>{label('docs.notUploaded')}</span>
              </li>
            ))}
          </ReminderSection>
        )}

        {sections.cargo && (
          <ReminderSection tone="info" icon={LuTruck} title={label('cargo.title')} count={cargo.length}
            description={label('cargo.description')} actionLabel={label('cargo.action')} onAction={goToCargo}>
            {cargo.slice(0, MAX_VISIBLE_CARGO).map((shipment) => <CargoReminderItem key={shipment.id} shipment={shipment} />)}
            {cargo.length > MAX_VISIBLE_CARGO && <li className="tw-px-3.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('cargo.more', { count: cargo.length - MAX_VISIBLE_CARGO })}</li>}
          </ReminderSection>
        )}
      </div>
    </Modal>
  );
};

export default LoginReminders;
